/**
 * Pure, framework-free helpers for the embed search widget's filter state.
 * Kept separate from the component so filter-building/counting logic is
 * unit-testable without pulling in React Testing Library (not used in this
 * repo - see src/helpers/*.test.js for the existing pattern).
 */

// Groups that narrow the feed via a multi-select id list (server side these
// become `financeType`, `timeline`, `purchaseType`, `propertyType` - comma
// joined lists of ids, same convention as parseIntListParam on the API).
export const FILTER_GROUPS = [
  "propertyType",
  "financeType",
  "timeline",
  "purchaseType",
];

// Every filter key the public widget feed will honor at all - must match
// Gitcha-API's widgetPublic.js WIDGET_FILTER_ALLOWLIST exactly. A widget's
// filters_config.filters (see resolveEnabledWidgetFilters) can only ever
// narrow this set, never widen it.
export const WIDGET_FILTER_KEYS = [
  "search",
  ...FILTER_GROUPS,
  "preApproved",
];

// The only sorts the public widget feed ever supports (server: widgetPublic
// .js's ALLOWED_SORTS) - the single source of truth for both the value sent
// to the API and the label shown in the sort dropdown.
export const DEFAULT_SORT_OPTIONS = [
  { value: "newest", label: "Newest" },
  { value: "oldest", label: "Oldest" },
];

/**
 * Which of WIDGET_FILTER_KEYS this widget's config exposes. Mirrors the
 * API's own default: a widget with no `filters` array (every widget created
 * before per-widget curation shipped) exposes everything, so the UI matches
 * what the server will actually honor rather than showing dead controls.
 */
export function resolveEnabledWidgetFilters(filtersConfig) {
  const configured = filtersConfig?.filters;
  if (!Array.isArray(configured)) {
    return new Set(WIDGET_FILTER_KEYS);
  }
  return new Set(WIDGET_FILTER_KEYS.filter((key) => configured.includes(key)));
}

/**
 * Resolves the sort control's enabled state, available options, and default
 * from a widget's filters_config.sort, falling back to "everything enabled"
 * for a widget with no sort descriptor at all. Never returns an empty
 * options list - an all-invalid configured list falls back to the full set,
 * matching the API's own fallback in widgetPublic.js.
 */
export function resolveWidgetSortConfig(filtersConfig) {
  const sort = filtersConfig?.sort;
  const configuredOptions = Array.isArray(sort?.options)
    ? DEFAULT_SORT_OPTIONS.filter((opt) => sort.options.includes(opt.value))
    : [];
  const options = configuredOptions.length
    ? configuredOptions
    : DEFAULT_SORT_OPTIONS;
  const defaultValue = options.some((opt) => opt.value === sort?.default)
    ? sort.default
    : options[0].value;
  return {
    enabled: sort?.enabled !== false,
    options,
    default: defaultValue,
  };
}

// A fresh, empty selection - one Set per group, nothing pre-selected.
export function createEmptyWidgetFilterState() {
  return {
    search: "",
    sortType: "newest",
    preApproved: false,
    selected: FILTER_GROUPS.reduce((acc, group) => {
      acc[group] = new Set();
      return acc;
    }, {}),
  };
}

/**
 * Flattens the widget's filter state into the flat query-param object the
 * public widget feed endpoint expects. Empty groups/search are omitted
 * entirely rather than sent as empty strings, so a widget with nothing
 * selected sends exactly the same request shape the plain list view does.
 */
export function buildWidgetSearchQuery(state, { page = 1, limit } = {}) {
  const params = {};

  const search = (state?.search || "").trim();
  if (search) params.search = search;

  if (state?.sortType) params.sortType = state.sortType;

  FILTER_GROUPS.forEach((group) => {
    const set = state?.selected?.[group];
    if (set && set.size > 0) {
      params[group] = Array.from(set).join(",");
    }
  });

  if (state?.preApproved) params.preApproved = "true";

  params.page = page;
  if (limit) params.limit = limit;

  return params;
}

/**
 * Number of filter groups actively narrowing the feed, for the "Filters (n)"
 * toggle badge. Search has its own visible input and isn't counted here -
 * mirrors src/helpers/activeFilterCount.js not double-counting the keyword
 * box against the "Filters" affordance.
 */
export function computeActiveWidgetFilterCount(state) {
  let count = 0;
  FILTER_GROUPS.forEach((group) => {
    const set = state?.selected?.[group];
    if (set && set.size > 0) count += 1;
  });
  if (state?.preApproved) count += 1;
  return count;
}

/**
 * Toggles a single value in/out of a filter group's Set, returning a new
 * state object (never mutates the input) so it drops straight into
 * useState's updater form.
 */
export function toggleWidgetFilterValue(state, group, value) {
  const current = state?.selected?.[group] || new Set();
  const next = new Set(current);
  next.has(value) ? next.delete(value) : next.add(value);
  return {
    ...state,
    selected: { ...state.selected, [group]: next },
  };
}
